import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowLeft, Check, ChevronDown } from "lucide-react";

/* ─── DESIGN TOKENS (matches GroupHome) ─── */
const C = {
  black:    "#0A0A0A",
  dark:     "#111111",
  card:     "#F8F7F5",
  cardBg:   "#FFFFFF",
  red:      "#F50000",
  redDark:  "#C00000",
  orange:   "#FF4A00",
  amber:    "#FF8A00",
  gold:     "#D88A00",
  white:    "#FFFFFF",
  offWhite: "#F5F3EF",
  gray:     "#888888",
  grayDim:  "#CCCCCC",
  border:   "rgba(0,0,0,0.08)",
  textDark: "#1A1A1A",
  textMid:  "#555555",
};

const BRAND_GRADIENT      = "linear-gradient(90deg,#D88A00,#FF8A00,#FF4A00,#F50000)";
const BRAND_GRADIENT_FULL = "linear-gradient(135deg,#0E0A01 0%,#5A3600 25%,#D88A00 50%,#FF4A00 75%,#F50000 100%)";
const HERO_GRADIENT       = "linear-gradient(135deg,#0E0A01 0%,#1A0D00 40%,#2A1500 70%,#3D1A00 100%)";

/* Font injection */
const fontHref = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = fontHref;
  document.head.appendChild(l);
}

/* ─── INVESTOR TIERS ─── */
const TIERS = [
  {
    id: "angel",
    label: "Angel",
    min: "£10,000",
    tag: "Entry",
    tagColor: C.gold,
    perks: [
      "Equity stake via SPV",
      "Quarterly portfolio reports",
      "Investor newsletter",
      "Annual Group update call",
    ],
    description: "For individuals backing the 4REVA vision at early stage.",
  },
  {
    id: "strategic",
    label: "Strategic",
    min: "£50,000",
    tag: "Most Popular",
    tagColor: C.orange,
    perks: [
      "Everything in Angel",
      "Direct subsidiary equity options",
      "Priority deal flow access",
      "Bi-annual 1:1 with Group leadership",
      "Co-investment rights on new ventures",
    ],
    description: "For professionals and family offices seeking active exposure.",
    featured: true,
  },
  {
    id: "institutional",
    label: "Institutional",
    min: "£250,000+",
    tag: "Premium",
    tagColor: C.red,
    perks: [
      "Everything in Strategic",
      "Board observer rights",
      "Custom SPV structuring",
      "Full due diligence pack",
      "Dedicated relationship manager",
      "Bespoke exit planning",
    ],
    description: "For funds, family offices, and institutional partners.",
  },
];

/* ─── FAQ ─── */
const FAQS = [
  {
    q: "How is 4REVA GROUP structured legally?",
    a: "4REVA GROUP OÜ is incorporated in Estonia under the EU legal framework (Reg. No. 17381555), with headquarters in London, UK. This gives investors the protection of EU corporate law and the transparency of UK-based operations.",
  },
  {
    q: "What does investing via an SPV mean?",
    a: "A Special Purpose Vehicle (SPV) is a separate legal entity created for each investment. It ring-fences your investment to a specific subsidiary or venture — meaning risk is isolated and you have a clean cap table entry without touching the wider group.",
  },
  {
    q: "What sectors does 4REVA operate in?",
    a: "Our current portfolio spans e-commerce, fintech, media, beauty, and security technology. New ventures are seeded regularly as part of the Group's digital platform strategy.",
  },
  {
    q: "Is there a minimum hold period?",
    a: "Typical hold periods are 3–7 years depending on the subsidiary. Each SPV will have clearly defined terms, exit mechanisms, and liquidity windows outlined in the investor agreement.",
  },
  {
    q: "How do I start the process?",
    a: "Submit the expression of interest form below. Our investor relations team will respond within 3 business days with a full information pack and a link to schedule a call.",
  },
];

/* ═══════════════════════
   FAQ ITEM
═══════════════════════ */
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      onClick={() => setOpen(v => !v)}
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        padding: "24px 0",
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 500, color: C.white }}>
          {q}
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} style={{ flexShrink: 0 }}>
          <ChevronDown size={18} color={C.orange} />
        </motion.div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              margin: "14px 0 0",
              fontSize: 14,
              lineHeight: 1.85,
              color: "rgba(255,255,255,0.52)",
              fontFamily: "'Outfit', sans-serif",
              overflow: "hidden",
            }}
          >
            {a}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════
   TIER CARD
═══════════════════════ */
function TierCard({ tier, index }: { tier: typeof TIERS[0]; index: number }) {
  const [hov, setHov] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.12, duration: 0.55 }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderRadius: 20,
        overflow: "hidden",
        background: tier.featured ? BRAND_GRADIENT_FULL : "#161616",
        border: tier.featured ? "none" : "1px solid rgba(255,255,255,0.07)",
        padding: "36px 32px",
        position: "relative",
        transform: hov || tier.featured ? "translateY(-6px)" : "translateY(0)",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
        boxShadow: hov || tier.featured
          ? "0 32px 64px rgba(0,0,0,0.45)"
          : "0 8px 24px rgba(0,0,0,0.2)",
      }}
    >
      {/* Tag */}
      <div style={{
        display: "inline-block",
        background: tier.featured ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.06)",
        border: `1px solid ${tier.tagColor}40`,
        borderRadius: 100,
        padding: "4px 14px",
        marginBottom: 20,
      }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.28em", textTransform: "uppercase", color: tier.tagColor, fontFamily: "'Outfit', sans-serif" }}>
          {tier.tag}
        </span>
      </div>

      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 600, color: C.white, margin: "0 0 6px" }}>
        {tier.label}
      </h3>
      <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: "0 0 24px", fontFamily: "'Outfit', sans-serif", lineHeight: 1.6 }}>
        {tier.description}
      </p>

      <div style={{ marginBottom: 28 }}>
        <span style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", fontFamily: "'Outfit', sans-serif" }}>
          Minimum Investment
        </span>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 600, color: C.white, lineHeight: 1.1, marginTop: 4 }}>
          {tier.min}
        </div>
      </div>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 24, marginBottom: 32 }}>
        {tier.perks.map((p, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 1,
              background: tier.featured ? "rgba(255,255,255,0.22)" : `${C.orange}22`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Check size={10} color={tier.featured ? C.white : C.orange} strokeWidth={2.5} />
            </div>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontFamily: "'Outfit', sans-serif", lineHeight: 1.5 }}>{p}</span>
          </div>
        ))}
      </div>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={() => document.querySelector("#invest-form")?.scrollIntoView({ behavior: "smooth" })}
        style={{
          width: "100%",
          background: tier.featured ? "rgba(255,255,255,0.15)" : BRAND_GRADIENT,
          border: tier.featured ? "1px solid rgba(255,255,255,0.3)" : "none",
          color: C.white,
          padding: "14px 0",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: "0.18em",
          cursor: "pointer",
          fontFamily: "'Outfit', sans-serif",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        REGISTER INTEREST <ArrowRight size={13} />
      </motion.button>
    </motion.div>
  );
}

/* ═══════════════════════
   EXPRESSION OF INTEREST FORM
═══════════════════════ */
function InvestForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", tier: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.tier) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 1200));
    setSubmitting(false);
    setSubmitted(true);
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    padding: "14px 18px",
    color: C.white,
    fontSize: 14,
    fontFamily: "'Outfit', sans-serif",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)",
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    display: "block",
    marginBottom: 8,
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{ textAlign: "center", padding: "80px 40px" }}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 18 }}
          style={{
            width: 72, height: 72, borderRadius: "50%",
            background: BRAND_GRADIENT,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 28px",
          }}
        >
          <Check size={30} color={C.white} strokeWidth={2.5} />
        </motion.div>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, color: C.white, margin: "0 0 14px", fontWeight: 400 }}>
          Expression Received
        </h3>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", lineHeight: 1.75, maxWidth: 400, margin: "0 auto", fontFamily: "'Outfit', sans-serif" }}>
          Thank you, {form.name.split(" ")[0]}. Our investor relations team will be in touch within 3 business days with your information pack.
        </p>
        <div style={{ marginTop: 32, fontSize: 11, letterSpacing: "0.22em", color: C.orange, fontFamily: "'Outfit', sans-serif" }}>
          4REVA GROUP · INVESTOR RELATIONS
        </div>
      </motion.div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      {/* Name */}
      <div>
        <label style={labelStyle}>Full Name *</label>
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Your full name"
          style={inputStyle}
          onFocus={e => e.target.style.borderColor = C.orange}
          onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
        />
      </div>

      {/* Email */}
      <div>
        <label style={labelStyle}>Email Address *</label>
        <input
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder="your@email.com"
          style={inputStyle}
          onFocus={e => e.target.style.borderColor = C.orange}
          onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
        />
      </div>

      {/* Company */}
      <div>
        <label style={labelStyle}>Company / Fund (optional)</label>
        <input
          name="company"
          value={form.company}
          onChange={handleChange}
          placeholder="Your organisation"
          style={inputStyle}
          onFocus={e => e.target.style.borderColor = C.orange}
          onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
        />
      </div>

      {/* Tier */}
      <div>
        <label style={labelStyle}>Investment Tier *</label>
        <select
          name="tier"
          value={form.tier}
          onChange={handleChange}
          style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}
          onFocus={e => e.target.style.borderColor = C.orange}
          onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
        >
          <option value="" style={{ background: "#1A1A1A" }}>Select a tier…</option>
          <option value="angel" style={{ background: "#1A1A1A" }}>Angel — from £10,000</option>
          <option value="strategic" style={{ background: "#1A1A1A" }}>Strategic — from £50,000</option>
          <option value="institutional" style={{ background: "#1A1A1A" }}>Institutional — £250,000+</option>
        </select>
      </div>

      {/* Message — full width */}
      <div style={{ gridColumn: "1 / -1" }}>
        <label style={labelStyle}>Tell us about yourself (optional)</label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          placeholder="Your investment background, sectors of interest, or questions…"
          rows={4}
          style={{ ...inputStyle, resize: "vertical" }}
          onFocus={e => e.target.style.borderColor = C.orange}
          onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
        />
      </div>

      {/* Submit */}
      <div style={{ gridColumn: "1 / -1" }}>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={handleSubmit}
          disabled={submitting || !form.name || !form.email || !form.tier}
          style={{
            background: (!form.name || !form.email || !form.tier) ? "rgba(255,255,255,0.08)" : BRAND_GRADIENT,
            color: C.white,
            border: "none",
            padding: "16px 48px",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "0.18em",
            cursor: (!form.name || !form.email || !form.tier) ? "not-allowed" : "pointer",
            fontFamily: "'Outfit', sans-serif",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 10,
            opacity: submitting ? 0.7 : 1,
            transition: "background 0.3s, opacity 0.2s",
          }}
        >
          {submitting ? "SUBMITTING…" : "SUBMIT EXPRESSION OF INTEREST"}
          {!submitting && <ArrowRight size={14} />}
        </motion.button>
        <p style={{ marginTop: 14, fontSize: 11, color: "rgba(255,255,255,0.3)", fontFamily: "'Outfit', sans-serif", lineHeight: 1.7 }}>
          By submitting you agree to be contacted by 4REVA GROUP investor relations. This is not a formal offer of securities. All investments carry risk.
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════
   MAIN PAGE
═══════════════════════ */
export default function InvestPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <>
      <Helmet>
        <title>Invest in 4REVA GROUP — Become an Investor</title>
        <meta name="description" content="Back 4REVA GROUP — an EU-registered technology holding company building digital platforms across multiple industries. Angel, Strategic, and Institutional investor tiers available." />
      </Helmet>

      <div style={{ backgroundColor: C.black, minHeight: "100vh", color: C.textDark, fontFamily: "'Outfit', sans-serif", overflowX: "hidden" }}>

        {/* ── NAV (back button) ── */}
        <motion.nav
          animate={{
            backgroundColor: scrolled ? "rgba(10,10,10,0.96)" : "transparent",
            backdropFilter: scrolled ? "blur(16px)" : "blur(0px)",
          }}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
            padding: "0 40px", height: 72,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
          }}
        >
          <motion.button
            whileHover={{ x: -3 }}
            onClick={() => navigate("/")}
            style={{ background: "none", border: "none", color: C.white, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontFamily: "'Outfit', sans-serif", opacity: 0.7 }}
          >
            <ArrowLeft size={16} /> Back to Group
          </motion.button>

          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, letterSpacing: "0.22em", color: C.white, fontWeight: 600 }}>
            4REVA GROUP
          </div>

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => document.querySelector("#invest-form")?.scrollIntoView({ behavior: "smooth" })}
            style={{
              background: BRAND_GRADIENT, color: C.white, border: "none",
              padding: "10px 24px", fontWeight: 700, fontSize: 11,
              letterSpacing: "0.14em", cursor: "pointer",
              fontFamily: "'Outfit', sans-serif", borderRadius: 4,
            }}
          >
            REGISTER INTEREST
          </motion.button>
        </motion.nav>

        {/* ── HERO ── */}
        <section style={{
          background: HERO_GRADIENT,
          minHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          padding: "140px 6% 80px",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Animated gradient orbs */}
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute", top: "15%", right: "10%",
              width: 600, height: 600, borderRadius: "50%",
              background: "radial-gradient(circle, #D88A0040 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            style={{
              position: "absolute", bottom: "20%", right: "25%",
              width: 400, height: 400, borderRadius: "50%",
              background: "radial-gradient(circle, #F5000030 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Accent line */}
          <div style={{ position: "absolute", top: 0, left: 0, width: 5, height: "100%", background: BRAND_GRADIENT }} />

          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <motion.p
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
              style={{
                fontSize: 10, letterSpacing: "0.45em", fontWeight: 700,
                textTransform: "uppercase", marginBottom: 20,
                background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                fontFamily: "'Outfit', sans-serif",
              }}
            >
              INVESTOR RELATIONS · 4REVA GROUP
            </motion.p>

            <h1 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(52px, 7vw, 108px)",
              fontWeight: 300,
              lineHeight: 1.0,
              margin: "0 0 12px",
              color: C.white,
              maxWidth: 800,
            }}>
              Back the next<br />
              <em style={{
                background: BRAND_GRADIENT,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontStyle: "italic",
                fontWeight: 400,
              }}>
                generation
              </em>
              <span style={{ color: C.white }}> of platforms.</span>
            </h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
              style={{ fontSize: 16, lineHeight: 1.8, color: "rgba(255,255,255,0.55)", maxWidth: 540, margin: "24px 0 48px", fontFamily: "'Outfit', sans-serif" }}
            >
              4REVA GROUP is an EU-registered technology holding company building digital platforms across e-commerce, fintech, media, beauty, and security. We're opening select investor access to the Group and its subsidiaries.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
              style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}
            >
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => document.querySelector("#tiers")?.scrollIntoView({ behavior: "smooth" })}
                style={{
                  background: BRAND_GRADIENT, color: C.white, border: "none",
                  padding: "18px 44px", fontWeight: 700, fontSize: 12,
                  letterSpacing: "0.16em", cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif", borderRadius: 4,
                  display: "flex", alignItems: "center", gap: 10,
                }}
              >
                VIEW INVESTOR TIERS <ArrowRight size={14} />
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => document.querySelector("#invest-form")?.scrollIntoView({ behavior: "smooth" })}
                style={{
                  background: "transparent", color: C.white,
                  border: "1px solid rgba(255,255,255,0.28)",
                  padding: "18px 44px", fontWeight: 500, fontSize: 12,
                  letterSpacing: "0.16em", cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif", borderRadius: 4,
                  transition: "border-color 0.25s",
                }}
              >
                REGISTER INTEREST
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.7 }}
            style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}
          >
            <div style={{ width: 1, height: 40, background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.28))" }} />
            <span style={{ fontSize: 9, letterSpacing: "0.35em", color: "rgba(255,255,255,0.28)", textTransform: "uppercase", fontFamily: "'Outfit', sans-serif" }}>Scroll</span>
          </motion.div>
        </section>

        {/* ── KEY NUMBERS BAR ── */}
        <div style={{ background: BRAND_GRADIENT, padding: "22px 6%", display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 20 }}>
          {[
            { num: "EU",       label: "Legal Framework" },
            { num: "Est. 2026", label: "Founded" },
            { num: "5+",        label: "Industries" },
            { num: "London",    label: "Headquarters" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, color: C.white, lineHeight: 1, fontWeight: 600 }}>{s.num}</div>
              <div style={{ fontSize: 10, letterSpacing: "0.22em", color: "rgba(255,255,255,0.75)", textTransform: "uppercase", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── WHY INVEST ── */}
        <section style={{ padding: "100px 6%", background: "#0E0E0E" }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            style={{ marginBottom: 64 }}
          >
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 14px", background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
              The Opportunity
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 60px)", fontWeight: 400, margin: 0, color: C.white, lineHeight: 1.1, maxWidth: 700 }}>
              Why invest in 4REVA GROUP
            </h2>
          </motion.div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 2 }}>
            {[
              {
                n: "01",
                title: "EU-Regulated Structure",
                body: "Incorporated in Estonia with UK headquarters — offering investor-grade legal transparency under EU corporate law.",
              },
              {
                n: "02",
                title: "Diversified Portfolio",
                body: "Multiple subsidiaries across distinct digital sectors reduce concentration risk while maintaining brand premium.",
              },
              {
                n: "03",
                title: "SPV Ring-Fencing",
                body: "Each investment is structured via its own SPV — your exposure is limited to the venture you back, not the whole group.",
              },
              {
                n: "04",
                title: "Brand Royalty Engine",
                body: "Subsidiaries pay royalties to the Group parent — creating a recurring, diversified income stream at the holding level.",
              },
              {
                n: "05",
                title: "Technology-First",
                body: "Every company in the portfolio is a digital-native business — built for scale, not geography.",
              },
              {
                n: "06",
                title: "Clear Exit Pathways",
                body: "Subsidiary IPOs, strategic acquisitions, or secondary sales provide defined liquidity windows for investors.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                style={{ background: "#131313", padding: "36px 32px", position: "relative", overflow: "hidden" }}
              >
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: i % 2 === 0 ? BRAND_GRADIENT : "transparent",
                }} />
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 13, color: C.orange, marginBottom: 16, opacity: 0.6, letterSpacing: "0.1em" }}>{item.n}</div>
                <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: C.white, margin: "0 0 12px", fontWeight: 600 }}>{item.title}</h4>
                <p style={{ fontSize: 13, lineHeight: 1.75, color: "rgba(255,255,255,0.48)", margin: 0, fontFamily: "'Outfit', sans-serif" }}>{item.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── TIERS ── */}
        <section id="tiers" style={{ padding: "100px 6%", background: C.black }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} style={{ marginBottom: 64 }}
          >
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 14px", background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
              Investor Tiers
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 60px)", fontWeight: 400, margin: 0, color: C.white, lineHeight: 1.1 }}>
              Choose your level of access
            </h2>
          </motion.div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {TIERS.map((tier, i) => (
              <TierCard key={tier.id} tier={tier} index={i} />
            ))}
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section style={{ padding: "100px 6%", background: "#0C0C0C" }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 64 }}>
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 14px", background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
              The Process
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 56px)", fontWeight: 400, margin: 0, color: C.white, lineHeight: 1.1 }}>
              From interest to investment
            </h2>
          </motion.div>

          <div style={{ display: "flex", flexDirection: "column", gap: 0, maxWidth: 700 }}>
            {[
              { step: "01", title: "Register your interest", body: "Complete the expression of interest form below. No commitment required at this stage." },
              { step: "02", title: "Receive your information pack", body: "Within 3 business days, you'll receive a full Group overview, portfolio summary, and SPV term sheet examples." },
              { step: "03", title: "Investor call with leadership", body: "A direct conversation with 4REVA GROUP leadership — no intermediaries. Ask anything." },
              { step: "04", title: "Due diligence & legal review", body: "We provide full documentation. Your legal team reviews. We move at your pace." },
              { step: "05", title: "SPV setup & funding", body: "Your SPV is structured, agreements signed, and funds transferred. You're in." },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                style={{ display: "flex", gap: 32, paddingBottom: 40, position: "relative" }}
              >
                {/* Connector line */}
                {i < 4 && (
                  <div style={{ position: "absolute", left: 19, top: 40, bottom: 0, width: 2, background: "rgba(255,255,255,0.06)" }} />
                )}
                <div style={{
                  width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                  background: BRAND_GRADIENT_FULL,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 700,
                  color: C.white, letterSpacing: "0.05em",
                  position: "relative", zIndex: 1,
                }}>
                  {item.step}
                </div>
                <div style={{ paddingTop: 8 }}>
                  <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: C.white, margin: "0 0 8px", fontWeight: 600 }}>{item.title}</h4>
                  <p style={{ fontSize: 14, lineHeight: 1.75, color: "rgba(255,255,255,0.48)", margin: 0, fontFamily: "'Outfit', sans-serif" }}>{item.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── FAQ ── */}
        <section style={{ padding: "100px 6%", background: C.black }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 56 }}>
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 14px", background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
              FAQ
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 56px)", fontWeight: 400, margin: 0, color: C.white, lineHeight: 1.1 }}>
              Common questions
            </h2>
          </motion.div>
          <div style={{ maxWidth: 760 }}>
            {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
        </section>

        {/* ── FORM ── */}
        <section id="invest-form" style={{ padding: "100px 6%", background: "#0C0C0C", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ maxWidth: 780, margin: "0 auto" }}>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 52, textAlign: "center" }}>
              <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 16px", background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
                Get Started
              </p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 56px)", fontWeight: 400, margin: "0 0 16px", color: C.white, lineHeight: 1.1 }}>
                Register your interest
              </h2>
              <p style={{ fontSize: 15, color: "rgba(255,255,255,0.45)", fontFamily: "'Outfit', sans-serif", lineHeight: 1.75, maxWidth: 500, margin: "0 auto" }}>
                No commitment. Just a conversation. Our investor relations team responds within 3 business days.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              style={{
                background: "#141414",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 20,
                padding: "48px 48px",
              }}
            >
              <InvestForm />
            </motion.div>

            {/* Legal note */}
            <div style={{ marginTop: 32, padding: "24px 28px", background: "rgba(255,255,255,0.03)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.05)" }}>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", lineHeight: 1.8, margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                <strong style={{ color: "rgba(255,255,255,0.45)" }}>Important:</strong> This page is for informational purposes only and does not constitute an offer or solicitation to sell or buy securities. All investments carry risk. Past performance of any portfolio company is not indicative of future results. 4REVA GROUP OÜ is incorporated in Estonia (Reg. No. 17381555). Please consult your financial and legal advisors before making any investment decision.
              </p>
            </div>
          </div>
        </section>

        {/* ── FOOTER MINI ── */}
        <footer style={{ background: "#080808", borderTop: "1px solid rgba(255,255,255,0.06)", padding: "32px 6%", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", margin: 0, fontFamily: "'Outfit', sans-serif" }}>
            © {new Date().getFullYear()} 4REVA GROUP®. All rights reserved.
          </p>
          <div style={{ display: "flex", gap: 24 }}>
            {[
              { label: "Privacy", path: "/privacy" },
              { label: "Terms",   path: "/terms" },
              { label: "Legal",   path: "https://4reva.com/legal" },
            ].map(l => (
              <span key={l.label}
                onClick={() => l.path.startsWith("http") ? window.open(l.path, "_blank") : navigate(l.path)}
                style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", cursor: "pointer", fontFamily: "'Outfit', sans-serif", transition: "color 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.color = C.orange)}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}
              >
                {l.label}
              </span>
            ))}
          </div>
        </footer>
      </div>

      <style>{`
        @media (max-width: 860px) {
          #invest-form > div > div:last-child > div {
            padding: 32px 24px !important;
          }
        }
        @media (max-width: 600px) {
          #invest-form > div > div:last-child > div > div {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}
